<template>
  <div class="users-list">
    <div class="py-2">
      <input
        v-if="!showConfigurable"
        id="searchUsersInput"
        ref="usersSearchInput"
        v-model="keyword"
        v-debounce="searchForUsersAndGroups"
        class="form-control"
        placeholder="Type to search for users and groups"
      >
    </div>
    <b-alert
      v-if="showAlert"
      show
      variant="info"
    >
      The list below displays the first 100 users and groups in alphabetical order.
      If you're looking for a specific user or group, please enter your search criteria to find them.
    </b-alert>
    <div v-if="showSelection">
      <div
        v-for="user of selectedUserObjects"
        :key="user.id"
        :data-name="user.displayName"
        class="selected-user-badge badge badge-primary py-1 px-2 mb-2 mr-2"
      >
        {{ user.displayName }}
        <span
          style="cursor: pointer"
          class="remove-selected-user ml-2"
          @click="userSelect(user)"
        ><i class="fas fa-times" /></span>
      </div>
    </div>
    <h4
      v-if="showConfigurable"
      class="text-center mb-5 mt-0"
    >
      Configured Users
    </h4>
    <content-spinner
      v-if="loading && showConfigurable"
      :show="true"
    />
    <div v-else>
      <div
        v-if="showConfigurable"
        class="d-flex align-items-center justify-content-end font-weight-bold mb-3 pt-2"
      >
        <div class="col-3 text-center mr-2">
          Choose field
        </div>
        <div class="col-4 text-center mr-1">
          Send Email Alerts
        </div>
      </div>
      <div class="search-users-options configured-users">
        <b-list-group-item
          v-for="user in (showConfigurable ? getUsers : users)"
          button
          class="d-flex align-items-center search-users-option selected-user-badge"
          :data-identity="user.isGroup ? 'group' : 'user'"
          :data-name="user.displayName"
          @click="userSelect(user)"
        >
          <b-avatar
            variant="primary"
            :text="getAvatarText(user)"
            class="mr-2"
          />
          <span class="mr-auto">{{ user.displayName }}</span>
          <b-form-select
            v-if="showConfigurable"
            id="todoListChooseField"
            :value="user.FieldToUseAsRecordNameGuid"
            style="width: 140px"
            :options="fields"
            :data-name="user.displayName"
            value-field="fieldGuid"
            text-field="displayName"
            @input="(val) => onFieldSelect(val, user)"
          />
          <b-form-checkbox
            v-if="showConfigurable"
            :id="`todoListSendEmailAlerts-${user.id}`"
            class="ml-5 mr-4"
            :value="true"
            :checked="user.SendEmailAlert"
            :unchecked-value="false"
            @input="(val) => onCheckboxChange(val, user)"
          />
          <font-awesome-icon
            v-if="!showConfigurable && selectedUsers.indexOf(user.id) > -1"
            class="text-success"
            :icon="['fas', 'check']"
          />
          <font-awesome-icon
            v-else-if="showConfigurable"
            class="remove-selected-user text-danger"
            :icon="['fas', 'times']"
            @click="handleUserRemove(user.userGuid)"
          />
        </b-list-group-item>
      </div>
    </div>
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import {axiosClient} from '../../../core/services/http.service';
import {SET_LOADING} from '@/store/modules/data-management/mutation-types';
import {SET_CONFIGURED_USERS, SET_CONFIGURED_USERS_LOADING} from '@/store/modules/user/mutation-types';
import ContentSpinner from '@/core/components/ContentSpinner.vue';

const {mapActions: mapActionsUser, mapState: mapStateUsers, mapMutations: mapMutationsUser} = createNamespacedHelpers('user');
const {mapMutations: mapMutationsDataManagement} = createNamespacedHelpers('dataManagement');

export default {
  name: 'UsersList',
  components: {ContentSpinner},
  props: {
    value: Array,
    showGroups: {
      type: Boolean,
      default: false,
    },
    showSelection: {
      type: Boolean,
      default: true,
    },
    showAlert: {
      type: Boolean,
      default: true,
    },
    showConfigurable: {
      type: Boolean,
      default: false,
    },
    fields: {
      type: Array,
      default: () => [],
    },
    usersProps: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      keyword: '',
      selectedUsers: [],
      selectedUserObjects: [],
    };
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        if (this.value) {
          this.selectedUsers = [...this.value];
        }
      },
    },
    selectedUsers: {
      deep: true,
      async handler() {
        this.populateSelectedUserObjects();
      },
    },
  },
  computed: {
    ...mapStateUsers({
      users: state => state.users.data,
      configuredUsers: state => state.configuredUsers.data,
      loading: state => state.configuredUsers.loading,
    }),
    filteredUsers() {
      return this.keyword ? this.users.filter(user => user.displayName.toLowerCase().includes(this.keyword.toLowerCase())) : this.users;
    },
    getUsers() {
      return this.usersProps.map(user => {
        const currentUser = this.configuredUsers.find(u => {
          return u.userGuid === user.UserGuid;
        });
        return ({...currentUser, ...user});
      }).map(user => ({...user, id: user.userGuid}));
    },
  },
  methods: {
    ...mapActionsUser(['getUsersAndGroups']),
    ...mapMutationsDataManagement({
      'setLoading': SET_LOADING,
    }),
    ...mapMutationsUser({
      'setConfiguredUsers': SET_CONFIGURED_USERS,
      'setConfiguredUsersLoading': SET_CONFIGURED_USERS_LOADING,
    }),
    getAvatarText(user) {
      if (!user.displayName) return '';
      return user.displayName.split(' ').map(el => el[0] ? el[0].toUpperCase() : '').join('');
    },
    userSelect(user) {
      if (!this.showConfigurable) {
        if (this.selectedUsers.indexOf(user.id) > -1) {
          this.selectedUsers = this.selectedUsers.filter(id => id !== user.id);
          this.$emit('userRemove', user);
          this.$emit('input', this.selectedUsers);
        } else {
          this.selectedUsers = [...this.selectedUsers, user.id];
          this.$emit('userSelect', user);
          this.$emit('input', this.selectedUsers);
        }
      }
    },
    getUserById(id) {
      return this.users.find(u => u.id == id) || {};
    },
    searchForUsersAndGroups() {
      this.getUsersAndGroups(this.keyword);
    },
    async populateSelectedUserObjects() {
      this.setConfiguredUsersLoading(true);
      const ids = this.selectedUsers.filter(id => id !== '00000000-0000-0000-0000-000000000000');
      if (ids.length) {
        const {body: users} = await axiosClient.post('api/aad/users', ids);

        this.setConfiguredUsers(users);

        this.selectedUserObjects = users.map(u => ({
          id: u.userGuid,
          displayName: u.displayName,
          mail: u.mail,
        }));
      }

      this.setConfiguredUsersLoading(false);
    },
    onFieldSelect(value, user) {
      this.$emit('onFieldSelected', [value, user]);
    },
    onCheckboxChange(value, user) {
      this.$emit('onCheckboxChanged', [value, user]);
    },
    handleUserRemove(userId) {
      this.$emit('onUserRemove', userId);
    },
  },
  async mounted() {
    if (!this.usersProps.length) {
      this.setLoading(true);
      await this.getUsersAndGroups();
      this.setLoading(false);
      this.populateSelectedUserObjects();
    }
  },
};
</script>

<style lang="scss" scoped>
.search-users-options {
  height: 300px;
  overflow: auto;
}
</style>
